import { authApi } from './api';
import type {
  Customer,
  License,
  Product,
  AdminUser,
  TrustedDevice,
  TrustedDevicePolicy,
  TrustedDevicePolicyInput,
  CustomerUser,
  CreateCustomerUserRequest,
  UpdateCustomerUserRequest
} from '../types';

export const adminService = {
  // === Customer Management ===
  async getCustomers(search?: string, page = 0, pageSize = 10): Promise<{ customers: Customer[] }> {
    const response = await authApi.get<{ customers: Customer[] }>('/api/customer', {
      params: { s: search, pageNo: page, pageSize }
    });
    return response.data;
  },

  async createCustomer(name: string): Promise<void> {
    await authApi.post('/api/customer/create', { name });
  },

  // === License Management ===
  async getLicenses(customerId?: string): Promise<{ licenses: License[] }> {
    const response = await authApi.get<{ licenses: License[] }>('/api/license/v1', {
      params: customerId ? { customerId } : undefined
    });
    return response.data;
  },

  async createLicense(customerId: string, productId: string): Promise<void> {
    await authApi.post('/api/license/v1', { customerId, productId });
  },

  // === User Management (Global) ===
  async getUsers(): Promise<{ users: AdminUser[] }> {
    const response = await authApi.get<{ users: AdminUser[] }>('/api/admin/user');
    return response.data;
  },

  // === Customer User Management ===
  async getCustomerUsers(customerId: string): Promise<{ customerUsers: CustomerUser[] }> {
    const response = await authApi.get<{ customerUsers: CustomerUser[] }>(`/api/customer/${customerId}/users`);
    return response.data;
  },

  async addCustomerUser(customerId: string, request: CreateCustomerUserRequest): Promise<void> {
    await authApi.post(`/api/customer/${customerId}/users`, request);
  },

  async updateCustomerUser(customerId: string, userId: string, request: UpdateCustomerUserRequest): Promise<void> {
    await authApi.put(`/api/customer/${customerId}/users/${userId}`, request);
  },

  async removeCustomerUser(customerId: string, userId: string): Promise<void> {
    await authApi.delete(`/api/customer/${customerId}/users/${userId}`);
  },

  // === Global Admin Management ===
  async toggleGlobalAdmin(username: string): Promise<{ message: string; isGlobalAdmin: boolean }> {
    const response = await authApi.post<{ message: string; isGlobalAdmin: boolean }>(
      `/api/admin/user/${encodeURIComponent(username)}/toggle-global-admin`
    );
    return response.data;
  },

  // === Trusted devices (2FA "husk denne enheten") ===
  async getTrustedDevices(username: string): Promise<{ devices: TrustedDevice[] }> {
    const response = await authApi.get<{ devices: TrustedDevice[] }>(
      `/api/admin/user/${encodeURIComponent(username)}/trusted-devices`
    );
    return response.data;
  },

  /** Revokes every remembered browser for the user; their next login asks for a code again. */
  async revokeTrustedDevices(username: string): Promise<{ message: string }> {
    const response = await authApi.post<{ message: string }>(
      `/api/admin/user/${encodeURIComponent(username)}/trusted-devices/revoke`
    );
    return response.data;
  },

  /** Revokes a single remembered browser by its id from getTrustedDevices. */
  async revokeTrustedDevice(username: string, deviceId: string): Promise<{ message: string }> {
    const response = await authApi.post<{ message: string }>(
      `/api/admin/user/${encodeURIComponent(username)}/trusted-devices/${encodeURIComponent(deviceId)}/revoke`
    );
    return response.data;
  },

  // === Trusted device policy (GlobalAdmin) ===
  async getTrustedDevicePolicy(): Promise<TrustedDevicePolicy> {
    const response = await authApi.get<TrustedDevicePolicy>('/api/admin/security/trusted-devices');
    return response.data;
  },

  async updateTrustedDevicePolicy(policy: TrustedDevicePolicyInput): Promise<TrustedDevicePolicy> {
    const response = await authApi.put<TrustedDevicePolicy>('/api/admin/security/trusted-devices', policy);
    return response.data;
  },

  /** Revokes every remembered browser on the platform. Everyone types a code next time. */
  async revokeAllTrustedDevices(): Promise<{ message: string; revoked: number }> {
    const response = await authApi.post<{ message: string; revoked: number }>(
      '/api/admin/security/trusted-devices/revoke-all'
    );
    return response.data;
  },

  // === Products ===
  async getProducts(): Promise<{ products: Product[] }> {
    const response = await authApi.get<{ products: Product[] }>('/api/products');
    return response.data;
  },
};

export default adminService;
